export const STAGE1_MIN_TOPICS = 1;
export const STAGE1_MAX_TOPICS = 10;
/** Each topic has exactly one question (2 options). */
export const STAGE1_QUESTIONS_PER_TOPIC = 1;

export const STAGE1_LIMITS = {
  topicHeading: 20,
  questionHeading: 50,
  option1Answer: 50,
  option2Answer: 50,
  optionDescription: 150,
};

export function getStage1OptionAnswerLimit(optionIndex) {
  return optionIndex === 0 ? STAGE1_LIMITS.option1Answer : STAGE1_LIMITS.option2Answer;
}

export function defaultStage1Question() {
  return {
    heading: '',
    question_image: '',
    options: [
      { answer: '', description: '', image: '', hover_image: '', value: 20 },
      { answer: '', description: '', image: '', hover_image: '', value: 20 },
    ],
  };
}

export function defaultStage1Topic(index) {
  return {
    name: `Topic ${index + 1}`,
    questions: [defaultStage1Question()],
  };
}

/** 1–10 topics with text Q&A structure — used for admin display and save. */
export function normalizeStage1(raw = {}) {
  const src = raw && typeof raw === 'object' ? raw : {};
  let topics = [];

  if (Array.isArray(src.topics) && src.topics.length > 0) {
    topics = src.topics.slice(0, STAGE1_MAX_TOPICS).map((topic, ti) => {
      const qs = Array.isArray(topic?.questions) && topic.questions.length
        ? topic.questions
        : [defaultStage1Question()];
      return {
        name: String(topic?.name ?? `Topic ${ti + 1}`),
        questions: [
          (qs[0] ? {
            heading: String(qs[0]?.heading ?? qs[0]?.question ?? ''),
            question_image: String(qs[0]?.question_image ?? qs[0]?.questionImage ?? ''),
            options: [0, 1].map((oi) => ({
              answer: String(qs[0]?.options?.[oi]?.answer ?? qs[0]?.options?.[oi]?.label ?? ''),
              description: String(qs[0]?.options?.[oi]?.description ?? ''),
              image: String(qs[0]?.options?.[oi]?.image ?? ''),
              hover_image: String(
                qs[0]?.options?.[oi]?.hover_image ?? qs[0]?.options?.[oi]?.hoverImage ?? ''
              ),
              value: Math.max(0, Number(qs[0]?.options?.[oi]?.value) || 20),
            })),
          } : defaultStage1Question()),
        ],
      };
    });
  }

  while (topics.length < STAGE1_MIN_TOPICS) {
    topics.push(defaultStage1Topic(topics.length));
  }

  return {
    ...src,
    type: 'topic_poll',
    max_topics: STAGE1_MAX_TOPICS,
    max_questions: STAGE1_QUESTIONS_PER_TOPIC,
    start_popup_image: String(src.start_popup_image ?? ''),
    start_popup_text: String(src.start_popup_text ?? ''),
    end_popup_image: String(src.end_popup_image ?? ''),
    end_popup_text: String(src.end_popup_text ?? ''),
    stats_image: String(src.stats_image ?? 'stage1/Stats.png'),
    topics,
  };
}

export function normalizeStagesDataForAdmin(raw = {}) {
  const base = raw && typeof raw === 'object' ? raw : {};
  return {
    ...base,
    stage_1: normalizeStage1(base.stage_1),
    stage_2: normalizeStage2(base.stage_2),
    stage_3: normalizeStage3(base.stage_3),
    stage_7: normalizeStage7(base.stage_7),
  };
}

export function validateStage1(raw) {
  const stage = normalizeStage1(raw);
  const errors = [];

  stage.topics.forEach((topic, ti) => {
    const topicNum = ti + 1;
    const name = String(topic.name ?? '').trim();
    if (!name) {
      errors.push(`Topic ${topicNum}: heading is required.`);
    } else if (name.length > STAGE1_LIMITS.topicHeading) {
      errors.push(`Topic ${topicNum}: heading must be ${STAGE1_LIMITS.topicHeading} characters or less.`);
    }

    const q = topic.questions?.[0];
    const questionHeading = String(q?.heading ?? '').trim();
    if (!questionHeading) {
      errors.push(`Topic ${topicNum}: question heading is required.`);
    } else if (questionHeading.length > STAGE1_LIMITS.questionHeading) {
      errors.push(`Topic ${topicNum}: question heading must be ${STAGE1_LIMITS.questionHeading} characters or less.`);
    }

    [0, 1].forEach((oi) => {
      const opt = q?.options?.[oi] || {};
      const optNum = oi + 1;
      const answer = String(opt.answer ?? '').trim();
      const description = String(opt.description ?? '').trim();

      if (!answer) {
        errors.push(`Topic ${topicNum}, Option ${optNum}: answer is required.`);
      } else if (answer.length > getStage1OptionAnswerLimit(oi)) {
        errors.push(
          `Topic ${topicNum}, Option ${optNum}: answer must be ${getStage1OptionAnswerLimit(oi)} characters or less.`
        );
      }

      if (!description) {
        errors.push(`Topic ${topicNum}, Option ${optNum}: description is required.`);
      } else if (description.length > STAGE1_LIMITS.optionDescription) {
        errors.push(`Topic ${topicNum}, Option ${optNum}: description must be ${STAGE1_LIMITS.optionDescription} characters or less.`);
      }
    });
  });

  return { valid: errors.length === 0, errors };
}

export const STAGE2_MESSAGE_LIMIT = 250;
export const STAGE2_LIMITS = {
  heading: 250,
  subheading: 250,
  popupText: 200,
};
const STAGE2_PLANETS = ['earth', 'venus', 'moon', 'mars'];

export function normalizeStage2(raw = {}) {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    ...src,
    type: 'planets',
    heading: String(src.heading ?? ''),
    subheading: String(src.subheading ?? ''),
    start_popup_text: String(src.start_popup_text ?? ''),
    end_popup_text: String(src.end_popup_text ?? ''),
  };
}

export function validateStage2(raw) {
  const stage = normalizeStage2(raw);
  const errors = [];

  const heading = String(stage.heading ?? '').trim();
  if (!heading) {
    errors.push('Heading is required.');
  } else if (heading.length > STAGE2_LIMITS.heading) {
    errors.push(`Heading must be ${STAGE2_LIMITS.heading} characters or less.`);
  }

  const subheading = String(stage.subheading ?? '').trim();
  if (!subheading) {
    errors.push('Subheading is required.');
  } else if (subheading.length > STAGE2_LIMITS.subheading) {
    errors.push(`Subheading must be ${STAGE2_LIMITS.subheading} characters or less.`);
  }

  const startPopup = String(stage.start_popup_text ?? '').trim();
  if (startPopup.length > STAGE2_LIMITS.popupText) {
    errors.push(`Start popup text must be ${STAGE2_LIMITS.popupText} characters or less.`);
  }

  const endPopup = String(stage.end_popup_text ?? '').trim();
  if (endPopup.length > STAGE2_LIMITS.popupText) {
    errors.push(`End popup text must be ${STAGE2_LIMITS.popupText} characters or less.`);
  }

  STAGE2_PLANETS.forEach((planet) => {
    const label = planet.charAt(0).toUpperCase() + planet.slice(1);
    const message = String(stage[`${planet}_message`] ?? '').trim();
    if (!message) {
      errors.push(`${label}: message is required.`);
    } else if (message.length > STAGE2_MESSAGE_LIMIT) {
      errors.push(`${label}: message must be ${STAGE2_MESSAGE_LIMIT} characters or less.`);
    }
  });

  return { valid: errors.length === 0, errors };
}

export const STAGE3_MIN_SECTORS = 1;
export const STAGE3_MAX_SECTORS = 5;

export const STAGE3_LIMITS = {
  title: 50,
  subtitle: 200,
  popupText: 200,
  sectorLabel: 30,
  sectorText: 150,
};

export const STAGE3_DEFAULT_BASE_GDP = 13.17;

export function defaultStage3Sector(index) {
  return {
    image: '',
    label: `Sector ${index + 1}`,
    text: '',
  };
}

export function normalizeStage3(raw = {}) {
  const src = raw && typeof raw === 'object' ? raw : {};
  let sectors = [];

  if (Array.isArray(src.sectors) && src.sectors.length > 0) {
    sectors = src.sectors.slice(0, STAGE3_MAX_SECTORS).map((sec, si) => ({
      image: String(sec?.image ?? ''),
      label: String(sec?.label ?? `Sector ${si + 1}`),
      text: String(sec?.text ?? ''),
    }));
  }

  while (sectors.length < STAGE3_MIN_SECTORS) {
    sectors.push(defaultStage3Sector(sectors.length));
  }

  return {
    ...src,
    type: 'economy',
    max_sectors: STAGE3_MAX_SECTORS,
    title: String(src.title ?? ''),
    subtitle: String(src.subtitle ?? ''),
    instruction: String(src.instruction ?? ''),
    start_popup_text: String(src.start_popup_text ?? ''),
    end_popup_text: String(src.end_popup_text ?? ''),
    money_bag_image: String(src.money_bag_image ?? ''),
    coin_image: String(src.coin_image ?? ''),
    base_gdp: Math.max(0, Number(src.base_gdp) || STAGE3_DEFAULT_BASE_GDP),
    sectors,
  };
}

export function validateStage3(raw) {
  const stage = normalizeStage3(raw);
  const errors = [];

  const title = String(stage.title ?? '').trim();
  if (!title) {
    errors.push('Title is required.');
  } else if (title.length > STAGE3_LIMITS.title) {
    errors.push(`Title must be ${STAGE3_LIMITS.title} characters or less.`);
  }

  const subtitle = String(stage.subtitle ?? '').trim();
  if (!subtitle) {
    errors.push('Subtitle is required.');
  } else if (subtitle.length > STAGE3_LIMITS.subtitle) {
    errors.push(`Subtitle must be ${STAGE3_LIMITS.subtitle} characters or less.`);
  }

  const startPopup = String(stage.start_popup_text ?? '').trim();
  if (!startPopup) {
    errors.push('Start popup text is required.');
  } else if (startPopup.length > STAGE3_LIMITS.popupText) {
    errors.push(`Start popup text must be ${STAGE3_LIMITS.popupText} characters or less.`);
  }

  const endPopup = String(stage.end_popup_text ?? '').trim();
  if (!endPopup) {
    errors.push('End popup text is required.');
  } else if (endPopup.length > STAGE3_LIMITS.popupText) {
    errors.push(`End popup text must be ${STAGE3_LIMITS.popupText} characters or less.`);
  }

  stage.sectors.forEach((sec, si) => {
    const sectorNum = si + 1;
    const label = String(sec.label ?? '').trim();
    const isDefaultLabel = label.toLowerCase() === `sector ${sectorNum}`;
    if (!label || isDefaultLabel) {
      errors.push(`Economy topic ${sectorNum}: label is required.`);
    } else if (label.length > STAGE3_LIMITS.sectorLabel) {
      errors.push(`Economy topic ${sectorNum}: label must be ${STAGE3_LIMITS.sectorLabel} characters or less.`);
    }

    const text = String(sec.text ?? '').trim();
    if (!text) {
      errors.push(`Economy topic ${sectorNum}: description is required.`);
    } else if (text.length > STAGE3_LIMITS.sectorText) {
      errors.push(`Economy topic ${sectorNum}: description must be ${STAGE3_LIMITS.sectorText} characters or less.`);
    }
  });

  const labels = stage.sectors.map((s) => String(s.label ?? '').trim().toLowerCase()).filter(Boolean);
  const dupes = labels.filter((l, i) => labels.indexOf(l) !== i);
  if (dupes.length > 0) {
    errors.push('Economy topic labels must be unique.');
  }

  return { valid: errors.length === 0, errors };
}

export const STAGE5_LIMITS = {
  question: 100,
  answer: 100,
  hint: 100,
  popupText: 200,
};

export const STAGE7_MAX_BACKGROUNDS = 3;
export const STAGE7_MAX_CLASS_BOARDS = 2;
export const STAGE7_MAX_TECHNOLOGIES = 4;
export const STAGE7_MAX_TECH_PICK = 3;

export const STAGE7_LIMITS = {
  title: 80,
  label: 30,
  popupText: 200,
};

const STAGE7_DEFAULT_BOARD_LABELS = ['Classic board', 'Digital board'];
const STAGE7_DEFAULT_TECH_LABELS = ['desk screens', 'Robot Teachers', 'Human Teachers', 'VR Headset'];

function defaultStage7Item(kind, index) {
  if (kind === 'background') {
    return { label: `Background ${index + 1}`, image: '' };
  }
  if (kind === 'class_board') {
    return { label: STAGE7_DEFAULT_BOARD_LABELS[index] || `Class board ${index + 1}`, image: '' };
  }
  return { label: STAGE7_DEFAULT_TECH_LABELS[index] || `Technology ${index + 1}`, image: '' };
}

function normalizeStage7List(rawList, kind, max) {
  const src = Array.isArray(rawList) ? rawList : [];
  const items = Array.from({ length: max }, (_, i) => {
    const item = src[i] && typeof src[i] === 'object' ? src[i] : {};
    const fallback = defaultStage7Item(kind, i);
    return {
      label: String(item.label ?? fallback.label),
      image: String(item.image ?? ''),
    };
  });
  return items;
}

export function normalizeStage7(raw = {}) {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    ...src,
    type: 'school_builder',
    title: String(src.title ?? 'Create the school of the future'),
    start_popup_text: String(src.start_popup_text ?? ''),
    end_popup_text: String(src.end_popup_text ?? ''),
    backgrounds: normalizeStage7List(src.backgrounds, 'background', STAGE7_MAX_BACKGROUNDS),
    class_boards: normalizeStage7List(src.class_boards, 'class_board', STAGE7_MAX_CLASS_BOARDS),
    technologies: normalizeStage7List(src.technologies, 'technology', STAGE7_MAX_TECHNOLOGIES),
    child_1_image: String(src.child_1_image ?? ''),
    child_2_image: String(src.child_2_image ?? ''),
    student_image: String(src.student_image ?? ''),
  };
}

export function validateStage7(raw) {
  const stage = normalizeStage7(raw);
  const errors = [];

  const title = String(stage.title ?? '').trim();
  if (!title) {
    errors.push('Title is required.');
  } else if (title.length > STAGE7_LIMITS.title) {
    errors.push(`Title must be ${STAGE7_LIMITS.title} characters or less.`);
  }

  const startPopup = String(stage.start_popup_text ?? '').trim();
  if (!startPopup) {
    errors.push('Start popup text is required.');
  } else if (startPopup.length > STAGE7_LIMITS.popupText) {
    errors.push(`Start popup text must be ${STAGE7_LIMITS.popupText} characters or less.`);
  }

  const endPopup = String(stage.end_popup_text ?? '').trim();
  if (!endPopup) {
    errors.push('End popup text is required.');
  } else if (endPopup.length > STAGE7_LIMITS.popupText) {
    errors.push(`End popup text must be ${STAGE7_LIMITS.popupText} characters or less.`);
  }

  const checkList = (items, groupName) => {
    items.forEach((item, i) => {
      const label = String(item.label ?? '').trim();
      if (!label) {
        errors.push(`${groupName} ${i + 1}: label is required.`);
      } else if (label.length > STAGE7_LIMITS.label) {
        errors.push(`${groupName} ${i + 1}: label must be ${STAGE7_LIMITS.label} characters or less.`);
      }
    });
  };

  checkList(stage.backgrounds, 'Background');
  checkList(stage.class_boards, 'Class board');
  checkList(stage.technologies, 'Technology');

  return { valid: errors.length === 0, errors };
}

export const STAGE4_MIN_MAPS = 10;
export const STAGE4_MAX_MAPS = 20;

export function validateStage4(raw) {
  const stage = raw && typeof raw === 'object' ? raw : {};
  const errors = [];

  const maps = Array.isArray(stage.maps) ? stage.maps : [];
  if (maps.length < STAGE4_MIN_MAPS) {
    errors.push(`At least ${STAGE4_MIN_MAPS} maps are required (currently ${maps.length}).`);
  }

  return { valid: errors.length === 0, errors };
}

export function validateStage5(raw) {
  const stage = raw && typeof raw === 'object' ? raw : {};
  const errors = [];

  const startPopup = String(stage.start_popup_text ?? '').trim();
  if (!startPopup) {
    errors.push('Start popup text is required.');
  } else if (startPopup.length > STAGE5_LIMITS.popupText) {
    errors.push(`Start popup text must be ${STAGE5_LIMITS.popupText} characters or less.`);
  }

  const endPopup = String(stage.end_popup_text ?? '').trim();
  if (!endPopup) {
    errors.push('End popup text is required.');
  } else if (endPopup.length > STAGE5_LIMITS.popupText) {
    errors.push(`End popup text must be ${STAGE5_LIMITS.popupText} characters or less.`);
  }

  const question = String(stage.question_text ?? stage.question ?? '').trim();
  if (!question) {
    errors.push('Question text is required.');
  } else if (question.length > STAGE5_LIMITS.question) {
    errors.push(`Question text must be ${STAGE5_LIMITS.question} characters or less.`);
  }

  const answer = String(stage.answer ?? '').trim();
  if (!answer) {
    errors.push('Answer is required.');
  } else if (answer.length > STAGE5_LIMITS.answer) {
    errors.push(`Answer must be ${STAGE5_LIMITS.answer} characters or less.`);
  }

  const hint = String(stage.hint_text ?? stage.hint ?? '').trim();
  if (!hint) {
    errors.push('Hint text is required.');
  } else if (hint.length > STAGE5_LIMITS.hint) {
    errors.push(`Hint text must be ${STAGE5_LIMITS.hint} characters or less.`);
  }

  return { valid: errors.length === 0, errors };
}
